// src/components/padrekko/qc/QcFindingCard.jsx
//
// One per finding. Renders in QcInspector's Findings section, below
// QcPolygonDrawer. Card tone follows the status color — NON_COMPLIANT
// reads red, VERIFY reads amber, ACCEPTABLE reads emerald — so the
// panel and the polygon on the map share a tone language.
//
// Three display states:
//
//   1. COLLAPSED (default)
//      Header:     Finding N · [feature]        [STATUS] [CORRECTED?] [v]
//      Body:       (none)
//      Action row: [Zoom] [Correct] [Dismiss]   hover/focus-within reveal
//
//   2. EXPANDED (click anywhere on the card body to toggle)
//      Header:     Finding N · [feature]        [STATUS] [CORRECTED?] [^]
//      Body:       [time]
//                  [framework chip]
//                  [regulation — prominent mono citation]
//                  [description]
//                  previously: X     (only if operator_trained)
//                  [SEVERITY chip] [CONFIDENCE chip]
//      Action row: same hover/focus-within reveal
//
//   3. CORRECT (when openCorrectFindingKey === this card's key)
//      Forces expansion regardless of isExpanded. No chevron — header
//      isn't toggleable in correct mode. Body is the inline correction:
//        "Describe what this feature actually is:" header
//        textarea (placeholder is the verbatim 6 examples from the
//          plugin's EditRetrainDialog — These were written because they
//          teach operators what makes a good correction)
//        hint: "AI will automatically determine: framework
//          (UL/RRC/TCEQ/NM OCD), regulation citation, compliance
//          status, and required action."
//        [Cancel] [Submit] — Submit shows Loader2 + "Resubmitting…"
//          when reanalyzeInflight.has(findingKey).
//
// Toggle behavior:
//   - Click the title rows (header / feature) or the collapsed body →
//     toggles isExpanded (unless in correct mode). The expanded body is NOT
//     a toggle zone, so attachment/note/input interactions don't collapse it.
//   - Action buttons stopPropagation so they don't toggle on click
//   - Click Correct on a collapsed card → openCorrect() forces expanded
//     AND switches to correct mode in one click (no toggle needed)
//   - Closing correct mode (Cancel, Submit success, or another card
//     stealing the open slot) collapses this card back to default —
//     simpler than restoring the prior isExpanded.
//
// Reanalyze image: when the operator submits, we use the same clean-
// canvas dance as the polygon drawer — setDrawingPolygon(true) hides
// QcFindingsLayer AND QcCurrentPadLayer, await once('idle'), snapshot,
// reset flag. AI sees pad imagery only, no overlays. Matches
// analyze-prompt tuning.
//
// Per-card local state: just correctionText. The "which card is open"
// flag is store-resident (useQcStore.openCorrectFindingKey) so opening
// one card auto-closes whichever was open. Cancel resets both the
// store flag AND local text — ready for a fresh open without stale
// contents.

import { useEffect, useRef, useState } from "react";
import {
  Crosshair, Pencil, X as XIcon, Loader2,
  RotateCcw, ChevronDown, ChevronUp,
  AlertTriangle, Check, Eye, EyeOff, MessageSquare, Move, Trash2,
  BadgeCheck, History,
} from "lucide-react";

import GatedAction from "./GatedAction";
import ValidationState, { ValidationPill, formatStamp } from "./ValidationState";
import { useAppViewStore } from "../../../stores/useAppViewStore";
import { useQcStore, findingKey } from "../../../stores/useQcStore";
import { correctionStages } from "./correctionStages";
import { useEffectiveOrg } from "../../../hooks/useEffectiveOrg";
import FindingNotesSection from "./FindingNotesSection";
import AttachmentSection from "./AttachmentSection";
import FindingCompletionSection from "./FindingCompletionSection";
import MandatoryReasonModal from "./MandatoryReasonModal";
import FindingAuditTrail from "./FindingAuditTrail";

// The chain is width-constrained, so it uses the SHORT set — which is the
// short form of the NEW names ("Owner", not "Customer"). roleLabels.js owns it,
// including the `system` key, which is a real by_role value and not a role.
import { CASE_ROLE_SHORT } from "../../../../../admin-api/lib/padrekko-qc/roleLabels.js";

// Verbatim from ul_hitl_v2.py:781. These examples have been tuned to teach
// operators what makes a good correction (specific feature + position
// + regulatory implication). DO NOT paraphrase, reorder, or add to.
const CORRECTION_PLACEHOLDER = `Examples:
  'This is a buried flowline scar running NW-SE, not a road edge'
  'This is an open reserve pit, liner still visible on north side'
  'This is traffic discoloration near the road entry, not contamination'
  'This is a tarp-covered mound, unknown contents, possible hazmat'
  'These are layflats/purple pipe segments needing removal'
  'This is bare unripped pad surface, no reclamation performed'

AI will determine the correct framework, regulation, and action.`;

// Verbatim from the plugin's hint label below the textarea.
const CORRECTION_HINT =
  "AI will automatically determine: framework (UL/RRC/TCEQ/NM OCD), " +
  "regulation citation, compliance status, and required action.";

// ── tone tables ────────────────────────────────────────────────────────────
const STATUS_TONES = {
  ACCEPTABLE:    "bg-emerald-500/10 border-emerald-500/30 text-emerald-200",
  VERIFY:        "bg-amber-500/10   border-amber-500/30   text-amber-200",
  NON_COMPLIANT: "bg-red-500/10     border-red-500/30     text-red-200",
};
const STATUS_PILL_TONES = {
  ACCEPTABLE:    "bg-emerald-500/20 border-emerald-500/50 text-emerald-100",
  VERIFY:        "bg-amber-500/20   border-amber-500/50   text-amber-100",
  NON_COMPLIANT: "bg-red-500/20     border-red-500/50     text-red-100",
};
// Severity: HIGH = bad → red. Confidence: HIGH = good → emerald.
const SEV_TONES = {
  HIGH:   "bg-red-500/15    border-red-500/40    text-red-200",
  MEDIUM: "bg-amber-500/15  border-amber-500/40  text-amber-200",
  LOW:    "bg-slate-700/30  border-slate-600/40  text-slate-300",
};
const CONF_TONES = {
  HIGH:   "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
  MEDIUM: "bg-amber-500/15   border-amber-500/40   text-amber-200",
  LOW:    "bg-slate-700/30   border-slate-600/40   text-slate-300",
};
const FALLBACK_TONE = "bg-slate-700/30 border-slate-600/40 text-slate-300";

const MICRO_PILL =
  "text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border whitespace-nowrap";

// Icon-only variant of MICRO_PILL, for the header's right-hand state group.
//
// The strip runs in ~317px (section[data-layer-panel] is calc(20rem + 50px),
// less panel and card padding). Five worded pills need ~390px, so they
// overflowed — and because the left group had no overflow-hidden, the nowrap
// "Finding N" painted UNDER them rather than truncating.
//
// Words traded for width, deliberately and with a cost: these are legible by
// glyph in colour, and NOT in grayscale. A printed export or a screenshot in a
// report loses the distinction. Every one carries a title and an aria-label so
// the fact survives hover and assistive tech, but not ink.
const ICON_PILL =
  "text-[9px] rounded border whitespace-nowrap flex items-center justify-center p-[3px] shrink-0";

// ── small render helpers ───────────────────────────────────────────────────
function StatusPill({ status }) {
  const tone = STATUS_PILL_TONES[status] || FALLBACK_TONE;
  return <span className={`${MICRO_PILL} ${tone}`}>{status}</span>;
}
// Correction badge. One outer pill holding a lit chip per review stage that
// has corrected this finding ([D] DH, [C] Customer, [O] Operator, always in
// that order) followed by the CORRECTED label — so the badge grows as the
// finding moves down the pipeline: [D] CORRECTED → [D][C] CORRECTED →
// [D][C][O] CORRECTED.
//
// Lit-only: stages that haven't corrected render nothing, they aren't
// placeholders. Chips carry their own title, so hovering a chip names that
// stage while hovering the label gives the whole-badge summary.
//
// The trigger is still analysis.operator_trained (unchanged) — chips are
// derived separately from history. A trained finding whose history can't be
// resolved to pipeline roles (legacy rows, missing by_role) renders the
// outer badge with no chips, which reads as today's badge minus "Reviewer".
function CorrectionStagePill({ history }) {
  const stages = correctionStages(history);
  const summary = stages.length
    ? `Corrected by ${stages.map((s) => s.label).join(", ")} — AI's interpretation has been refined.`
    : "Reanalyzed with a reviewer correction — AI's interpretation has been refined.";
  return (
    <span
      className={`${MICRO_PILL} bg-purple-500/15 border-purple-500/40 text-purple-200
                  flex items-center gap-1 px-1`}
      title={summary}
    >
      {stages.map((s) => (
        <span
          key={s.letter}
          className="text-[9px] font-semibold tracking-normal leading-none
                     px-1 py-0.5 rounded-sm border
                     bg-purple-500/30 border-purple-400/50 text-purple-100"
          title={[
            `${s.label} corrected`,
            s.count > 1 ? `${s.count}×` : null,
            formatTime(s.at) || null,
          ].filter(Boolean).join(" · ")}
        >
          {s.letter}
        </span>
      ))}
      <span className="pr-0.5">Corrected</span>
    </span>
  );
}
// Phase A — disputed indicator pill. Appears next to status when the
// finding has been flagged via a reverse-stage transition.
function DisputedPill() {
  return (
    <span
      className={`${MICRO_PILL} bg-amber-500/15 border-amber-500/50 text-amber-200
                  flex items-center gap-1`}
      title="Flagged for review on a reverse-stage transition."
    >
      <AlertTriangle size={9} />
      Disputed
    </span>
  );
}
// Phase 2 — kicked-back indicator. Shown to all roles when a finding has been
// sent back to DH for rework (the case stays at customer_review until DH
// resubmits). Teal to distinguish from the amber Disputed pill.
function KickedBackPill() {
  return (
    <span
      className={`${MICRO_PILL} bg-teal-500/15 border-teal-500/50 text-teal-200
                  flex items-center gap-1`}
      title="Sent back to DreamHammer for rework."
    >
      <RotateCcw size={9} />
      With DH
    </span>
  );
}

// The dispute banner. Since 100 it renders TWO states — an OPEN objection and
// a RESOLVED one — because the objection now survives its own ruling.
//
// ── WHO MAY DO WHAT, AND WHY IT IS NOT A SECOND COPY OF STAGE_WRITER ──────
//
// A dispute is a sender's flag on a receiver. Ending one has three outcomes and
// two parties:
//
//   uphold / overrule   the RECEIVER rules on the objection
//   withdraw            the RAISER takes it back
//
// The receiver test is `caseWriteable && !isRaiser`. caseWriteable is the
// SERVER'S OWN ANSWER to "is this caller the current stage's writer" — it comes
// off GET /cases/:id as `writeable`, computed by requireCaseAccess from
// STAGE_WRITER. Deriving it again here from an imported table would be a second
// copy of the thing REM-301 spent a phase collapsing to one, and it would be a
// copy that could disagree with the gate that actually decides.
//
// THE BUTTONS ARE A COURTESY, NOT THE BOUNDARY. The server refuses with a 409
// naming the party whose act it is, and that text is what this surface shows on
// failure. Before 100 there was no boundary at all: the button rendered for
// every participant and the endpoint checked no role, so the org that raised a
// dispute could end its own.
//
// ── EVERY OUTCOME NEEDS A REASON, WITHDRAW INCLUDED ───────────────────────
//
// 100's shape CHECK requires the note against every writer. The form asks for
// it before enabling the action rather than letting the server reject a click.
function DisputedBanner({
  finding, clearInflight, onResolve, isRaiser, canRule,
}) {
  const [pending, setPending] = useState(null);   // 'upheld' | 'overruled' | 'withdrawn'
  const [note, setNote] = useState("");
  const [err, setErr] = useState(null);

  const noteText  = finding.disputed_note ?? "";
  const flaggedAt = finding.disputed_at;
  const orgLabel  = finding.disputed_by_org_id
    ? (finding.disputed_by_org_name || "Unknown org")
    : null;

  const fmt = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
    } catch { return ""; }
  };

  async function submit() {
    const trimmed = note.trim();
    if (!trimmed) { setErr("A reason is required."); return; }
    setErr(null);
    const result = await onResolve(pending, trimmed);
    if (result?.ok) {
      setPending(null);
      setNote("");
    } else {
      setErr(result?.error || "Could not record the outcome.");
    }
  }

  const ACTIONS = canRule
    ? [
        { key: "upheld",    label: "Uphold",   hint: "The objection was right and has been addressed" },
        { key: "overruled", label: "Overrule", hint: "The objection does not stand — say why" },
      ]
    : isRaiser
      ? [{ key: "withdrawn", label: "Withdraw", hint: "Take your own objection back" }]
      : [];

  return (
    <div className="rounded ring-1 ring-amber-500/40 bg-amber-500/10 px-2.5 py-2 mb-2">
      <div className="flex items-start gap-1.5">
        <AlertTriangle size={11} className="text-amber-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold text-amber-100">
            Flagged for review
          </div>
          {noteText && (
            <div className="text-[10px] text-amber-200/90 mt-0.5 leading-snug break-words">
              "{noteText}"
            </div>
          )}
          <div className="text-[9px] text-amber-300/70 mt-0.5">
            {orgLabel ? `by ${orgLabel}` : "by sender"}
            {fmt(flaggedAt) && (<><span className="mx-1">·</span>{fmt(flaggedAt)}</>)}
          </div>
        </div>
      </div>

      {/* Nobody here can end it — say whose call it is rather than showing a
          control that would be refused. */}
      {ACTIONS.length === 0 && (
        <div className="text-[9px] text-amber-300/60 mt-1.5 leading-snug">
          {isRaiser
            ? "You raised this. Only the party the case is with can rule on it."
            : "Only the party the case is currently with can rule on this, or the org that raised it can withdraw it."}
        </div>
      )}

      {ACTIONS.length > 0 && pending === null && (
        <div className="flex items-center gap-1.5 mt-1.5">
          {ACTIONS.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={() => { setPending(a.key); setNote(""); setErr(null); }}
              disabled={clearInflight}
              title={a.hint}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                         border-amber-400/50 bg-amber-400/10 text-amber-100
                         hover:bg-amber-400/20
                         disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <Check size={10} />
              {a.label}
            </button>
          ))}
        </div>
      )}

      {pending !== null && (
        <div className="mt-1.5 space-y-1.5">
          <div className="text-[9px] font-mono uppercase tracking-widest text-amber-300/70">
            {pending === "upheld" ? "Uphold" : pending === "overruled" ? "Overrule" : "Withdraw"}
            {" — reason (required)"}
          </div>
          <textarea
            autoFocus
            rows={2}
            value={note}
            onChange={(e) => { setNote(e.target.value); if (err) setErr(null); }}
            disabled={clearInflight}
            placeholder="Recorded on the finding, beside the objection."
            className="w-full text-[10px] px-2 py-1.5 rounded border
                       border-amber-500/40 bg-slate-900 text-slate-100
                       placeholder:text-slate-600 focus:outline-none
                       focus:border-amber-400/70 disabled:opacity-50
                       leading-snug resize-none"
          />
          {err && (
            <div className="text-[9px] text-red-300 leading-snug">{err}</div>
          )}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={submit}
              disabled={clearInflight || !note.trim()}
              className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                         border-amber-400/50 bg-amber-400/10 text-amber-100
                         hover:bg-amber-400/20
                         disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {clearInflight
                ? <Loader2 size={10} className="animate-spin" />
                : <Check size={10} />}
              {clearInflight ? "Recording…" : "Record"}
            </button>
            <button
              type="button"
              onClick={() => { setPending(null); setNote(""); setErr(null); }}
              disabled={clearInflight}
              className="text-[10px] px-2 py-1 rounded border border-slate-700
                         text-slate-300 hover:bg-slate-800 transition
                         disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// A dispute that has been ruled on. Renders the WHOLE EXCHANGE — the objection
// as raised and the ruling on it — which is only possible since 100 stopped the
// clearing path nulling disputed_at, disputed_by_* and disputed_note.
const RESOLUTION_COPY = {
  upheld:    { label: "Objection upheld",  tone: "text-emerald-300", ring: "ring-emerald-500/30 bg-emerald-500/5" },
  overruled: { label: "Objection overruled", tone: "text-rose-300",  ring: "ring-rose-500/30 bg-rose-500/5" },
  withdrawn: { label: "Objection withdrawn", tone: "text-slate-300", ring: "ring-slate-600/40 bg-slate-700/10" },
};

function ResolvedDisputeBanner({ finding }) {
  const spec = RESOLUTION_COPY[finding.dispute_resolution];
  if (!spec) return null;
  const raiser = finding.disputed_by_org_name || null;
  const ruler  = finding.dispute_resolution_by_org_name || null;
  const fmt = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
    } catch { return ""; }
  };

  return (
    <div className={`rounded ring-1 ${spec.ring} px-2.5 py-2 mb-2`}>
      <div className="flex items-start gap-1.5">
        <Check size={11} className={`${spec.tone} mt-0.5 shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] font-semibold ${spec.tone}`}>{spec.label}</div>
          {finding.disputed_note && (
            <div className="text-[10px] text-slate-400 mt-0.5 leading-snug break-words italic">
              "{finding.disputed_note}"
              {raiser && <span className="not-italic text-[9px]"> — {raiser}{fmt(finding.disputed_at) ? `, ${fmt(finding.disputed_at)}` : ""}</span>}
            </div>
          )}
          {finding.dispute_resolution_note && (
            <div className="text-[10px] text-slate-300 mt-1 leading-snug break-words">
              {finding.dispute_resolution_note}
              {ruler && <span className="text-[9px] text-slate-500"> — {ruler}{fmt(finding.dispute_resolution_at) ? `, ${fmt(finding.dispute_resolution_at)}` : ""}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChipSev({ sev }) {
  if (!sev) return null;
  return <span className={`${MICRO_PILL} ${SEV_TONES[sev] || FALLBACK_TONE}`}>{sev} SEV</span>;
}
function ChipConf({ conf }) {
  if (!conf) return null;
  return <span className={`${MICRO_PILL} ${CONF_TONES[conf] || FALLBACK_TONE}`}>{conf} CONF</span>;
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

// Short actor labels for the per-finding provenance trail. by_role on each
// history entry is one of these; 'system' is the AI autodetect pass.

// Collapse a finding's history into an ordered actor chain
// ("AI → DH → Operator → Customer") for the provenance trail. Consecutive
// same-actor entries fold into one step (keeping the latest event + time), so
// the chain reads as distinct hand-offs, not raw event count. Returns [] for
// empty/missing history.
function buildProvenanceTrail(history) {
  if (!Array.isArray(history) || history.length === 0) return [];
  const steps = [];
  for (const h of history) {
    const role = h?.by_role || "system";
    const isValidation = h?.event === "validated";
    const prev = steps[steps.length - 1];
    // Same-actor folding keeps the chain reading as hand-offs rather than raw
    // event count — but a validation must never be folded, in either
    // direction. Folded INTO the previous step it disappears entirely; folding
    // the NEXT event into it overwrites the word "validated" with whatever came
    // after. Either way an affirmation that the whole capability exists to
    // record becomes invisible in the one panel that shows the audit trail.
    //
    // Note this was never a whitelist that dropped unknown event types — the
    // trail is keyed on by_role and would have shown a validation as a step in
    // its own right. The folding is what hid it, which is a quieter bug.
    if (prev && prev.role === role && !isValidation && !prev.isValidation) {
      prev.event = h?.event ?? prev.event;
      prev.at = h?.at ?? prev.at;
      continue;
    }
    steps.push({
      role,
      label: CASE_ROLE_SHORT[role] ?? role,
      event: h?.event,
      at: h?.at,
      orgId: h?.by_org_id ?? null,
      isValidation,
      userId: h?.by_user_id ?? null,
      userName: h?.by_user_name ?? null,
      orgName: h?.by_org_name ?? null,
      caseStage: h?.case_stage ?? null,
    });
  }
  return steps;
}

// ── card ───────────────────────────────────────────────────────────────────
export default function QcFindingCard({ padIdx, pad, finding }) {
  const mapInstance            = useAppViewStore((s) => s.mapInstance);
  const reanalyzeInflight      = useQcStore((s) => s.reanalyzeInflight);
  const openCorrectFindingKey  = useQcStore((s) => s.openCorrectFindingKey);
  const openCorrect            = useQcStore((s) => s.openCorrect);
  const closeCorrect           = useQcStore((s) => s.closeCorrect);
  const reanalyzeFinding       = useQcStore((s) => s.reanalyzeFinding);
  const submitPolygonEdit      = useQcStore((s) => s.submitPolygonEdit);
  const dismissFinding         = useQcStore((s) => s.dismissFinding);
  const zoomToFinding          = useQcStore((s) => s.zoomToFinding);
  const setDrawingPolygon      = useQcStore((s) => s.setDrawingPolygon);
  const resolveDispute         = useQcStore((s) => s.resolveDispute);
  const clearDisputeInflightFindingId =
    useQcStore((s) => s.clearDisputeInflightFindingId);
  const isClearingThisDispute  = finding.id && clearDisputeInflightFindingId === finding.id;
  // Read-only gate. When the caller's role doesn't match the case's current
  // stage writer, finding-modification affordances (Correct, Dismiss,
  // Submit) are disabled — the backend would reject the writes with
  // "Read-only at this stage" anyway, and the affordances are misleading.
  // Zoom (map-only), Cancel (UI close), and Mark-as-addressed (Phase A
  // participation-gated, intentionally cross-role) stay enabled.
  const caseWriteable          = useQcStore((s) => s.caseWriteable);
  const caseRole               = useQcStore((s) => s.caseRole);
  // Phase 2 — DH may work a finding the customer kicked back to it even though
  // the case sits at customer_review (DH isn't the stage writer there). Mirrors
  // the backend canWriteFindingWithKickback. Per-finding: a NON-kicked finding
  // still leaves DH read-only at customer_review (smoke C7).
  const isStageWriter          = caseWriteable;
  const isDhKickback           = caseRole === "dh_analyst" && finding.kicked_back_to_dh === true;
  const canModify              = isStageWriter || isDhKickback;
  const readOnlyTitle          = "Read-only at this stage — only the current stage owner can modify findings";

  // ── validate gating ──────────────────────────────────────────────────────
  //
  // Server-derived; the browser never inspects history to work this out.
  const validation             = finding.validation ?? null;
  const validationStateName    = validation?.state ?? "none";
  const validateInflightKey    = useQcStore((s) => s.validateInflightKey);
  const validateFinding        = useQcStore((s) => s.validateFinding);
  const isValidatingThis       = validateInflightKey === findingKey(pad?.id, finding.idx);

  // The headline validation only blocks the button when it is CURRENT. A stale
  // one must not — the prior affirmation no longer applies to what is on
  // screen, so re-validating is exactly the right thing to offer.
  const alreadyValidated       = validationStateName === "current";

  // Idempotency is keyed on (hash, role, stage) — NOT on user. So a colleague
  // at the same role and stage may be the one who validated this, and clicking
  // again would return 200 unchanged without recording the second name. The
  // copy therefore says who did it and never "you validated this", which would
  // be a claim about the reader that the record does not support.
  const validatedBy            = validation?.validation;
  const validateReason =
    !caseWriteable
      ? readOnlyTitle
      : alreadyValidated
      ? `Affirmed by ${validatedBy?.by_user_name || "another reviewer"}`
        + `${formatStamp(validatedBy?.at) ? `, ${formatStamp(validatedBy.at)}` : ""}`
      : null;
  const canValidate            = caseWriteable && !alreadyValidated && !isValidatingThis;

  // Phase 2 — kickback affordances.
  const kickBackFinding        = useQcStore((s) => s.kickBackFinding);
  const resubmitFinding        = useQcStore((s) => s.resubmitFinding);
  const resubmitInflightFindingId = useQcStore((s) => s.resubmitInflightFindingId);
  const isResubmittingThis     = finding.id && resubmitInflightFindingId === finding.id;

  // Persisted Remove (soft-delete). Gated to the stage OWNER of dh_review or
  // customer_review only — caseWriteable already means "caller is the current
  // stage writer", so adding the stage check excludes the operator at
  // operator_review (where it is the writer) and the 'complete' stage. Distinct
  // from Dismiss (client-only declutter); this persists, audits, and hides the
  // finding from every active surface.
  const removeFinding          = useQcStore((s) => s.removeFinding);
  const currentStage           = useQcStore((s) => s.caseInfo?.current_stage);
  // NOT widened for DH-kickback (Q7): DH addresses a kicked-back finding via
  // reanalyze/polygon-edit, never remove. Gated on the original stage writer.
  const canRemove              = isStageWriter &&
    (currentStage === "dh_review" || currentStage === "customer_review");

  // Customer may kick this finding back to DH (customer_review, not already
  // kicked, not removed). DH may resubmit a finding kicked back to it.
  const canKickBack = caseRole === "customer_analyst"
    && currentStage === "customer_review"
    && !finding.kicked_back_to_dh
    && !finding.is_removed;
  const canResubmit = isDhKickback;

  // Per-finding map polygon visibility. Session-local; always toggleable
  // regardless of caseWriteable (purely a viewing affordance, no server
  // write). Card stays in the panel; only the map polygon disappears.
  const hiddenFindingKeys        = useQcStore((s) => s.hiddenFindingKeys);
  const toggleFindingVisibility  = useQcStore((s) => s.toggleFindingVisibility);
  const isHidden                 = hiddenFindingKeys.has(findingKey(pad.id, finding.idx));

  // Day 8 Phase A — finding notes count. Drives the chip-row badge icon
  // (renders only when noteCount > 0). caseId pulled off caseInfo since
  // findings don't carry it; the card is always rendered inside a loaded
  // case context.
  const caseId                   = useQcStore((s) => s.caseInfo?.id);
  const notesByCase              = useQcStore((s) => s.notesByCase);
  const noteCount                = (caseId && finding.id)
    ? (notesByCase.get(caseId)?.findings.get(finding.id)?.length ?? 0)
    : 0;
  const notesSectionRef          = useRef(null);

  // Day 8 Phase 2 — DH service-provider polygon edit.
  const effectiveOrg             = useEffectiveOrg();
  const isServiceProvider        = effectiveOrg?.is_service_provider === true;
  // Phase 2 — FE mirror of the backend canEditPolygon: SP, the customer at
  // customer_review, or DH on a finding kicked back to it.
  const canEditPolygonFE =
    isServiceProvider ||
    (caseRole === "customer_analyst" && currentStage === "customer_review") ||
    isDhKickback;
  const polygonEditMode          = useQcStore((s) => s.polygonEditMode);
  const enterPolygonEditMode     = useQcStore((s) => s.enterPolygonEditMode);
  const exitPolygonEditMode      = useQcStore((s) => s.exitPolygonEditMode);
  // Match on (padIdx, findingIdx) — always-present integers — NOT findingId.
  // A freshly-created finding has no DB `id` until the case is reloaded, so
  // `polygonEditMode?.findingId === finding.id` was `undefined === undefined`
  // → permanently true: the pencil toggle could only ever EXIT edit mode (never
  // enter), the polygon never loaded into MlDraw, and Submit couldn't post an
  // edit. Reopening the case gave the finding an id and it worked — exactly the
  // reported behavior.
  const isEditingThisPolygon =
    !!polygonEditMode &&
    polygonEditMode.padIdx === padIdx &&
    polygonEditMode.findingIdx === finding.idx;

  const [correctionText, setCorrectionText] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  // Remove-finding rationale modal (mandatory reason). The reason/submit/error
  // state now lives inside MandatoryReasonModal; we only track open/closed.
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showKickbackModal, setShowKickbackModal] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);

  const key = findingKey(pad.id, finding.idx);
  const isOpen        = openCorrectFindingKey === key;
  const isReanalyzing = reanalyzeInflight.has(key);

  // Correct mode forces expansion regardless of operator's prior toggle.
  const effectiveExpanded = isOpen || isExpanded;

  // When correct mode closes (Cancel, Submit success, another card
  // taking the open slot), collapse this card and clear the textarea.
  // Tracked via a ref so we only fire on the open→close transition.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      setIsExpanded(false);
      setCorrectionText("");
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const analysis = finding.analysis || {};
  const operatorTrained = analysis.operator_trained === true;

  // "previously: X" reads from the LAST history entry's previous_analysis
  // — that's the snapshot of what the analysis was right before the most
  // recent reanalyze wrote over it.
  const lastHistory = finding.history?.[finding.history.length - 1];
  const prevStatus = operatorTrained
    ? lastHistory?.previous_analysis?.status
    : null;

  const createdAt = finding.history?.[0]?.at;
  const timeLabel = formatTime(createdAt);

  // Provenance trail — the chain of orgs/roles that touched this finding.
  // Only meaningful (and only shown) once more than one actor is involved
  // (e.g. AI → DH → Operator), surfacing the multi-org review story.
  const provTrail = buildProvenanceTrail(finding.history);

  const cardTone = STATUS_TONES[analysis.status] || FALLBACK_TONE;

  // Compressed regulation for the collapsed signal line. Most citations
  // ("UL Sec. XIII(d)", "RRC §3.91") fit comfortably; longer ones get
  // sliced + ellipsized so they don't push severity/confidence off-row.
  const REG_MAX_LEN = 32;
  const shortReg =
    analysis.regulation && analysis.regulation.length > REG_MAX_LEN
      ? analysis.regulation.slice(0, REG_MAX_LEN - 1) + "…"
      : analysis.regulation;

  // ─── actions ────────────────────────────────────────────────────────────
  // Title-row / collapsed-body click → toggle. Wired onto the header rows and
  // the collapsed summary only — NOT the expanded body — so interactive body
  // content (attachments, notes, caption inputs, lightbox/delete) doesn't
  // bubble up and collapse the card. Inactive in correct mode (isOpen).
  function handleCardClick() {
    if (isOpen) return;
    setIsExpanded((v) => !v);
  }

  function handleZoom() {
    zoomToFinding(pad.id, finding.idx);
  }
  function handleOpenCorrect() {
    openCorrect(key);
  }
  function handleCancelCorrect() {
    // Day 8 Phase 2 — also exit polygon edit if it was active for this
    // finding. Per spec note: cancel discards both polygon changes and
    // correction text. QcPolygonDrawer subscribes to polygonEditMode and
    // tears MlDraw down on the flip.
    if (isEditingThisPolygon) {
      exitPolygonEditMode();
    }
    closeCorrect();
    setCorrectionText("");
  }
  function handleTogglePolygonEdit() {
    if (isEditingThisPolygon) {
      exitPolygonEditMode();
    } else {
      // Capture the current polygon as the original (for Cancel restore).
      // QcPolygonDrawer reads it from the store and loads it into MlDraw.
      enterPolygonEditMode({
        findingId: finding.id,
        findingIdx: finding.idx,
        padIdx,
        originalPolygon: finding.polygon_geom,
      });
    }
  }
  function handleDismiss() {
    dismissFinding(pad.id, finding.idx);
  }
  // No optimistic update. Currency is decided by a content hash on the server,
  // so guessing the resulting state here would be a second derivation path in
  // the browser — one that can disagree with the record it claims to show.
  // The card re-renders from the response.
  async function handleValidate() {
    await validateFinding(pad.id, finding.idx);
  }
  function handleOpenRemove() {
    setShowRemoveModal(true);
  }
  function handleOpenKickback() {
    setShowKickbackModal(true);
  }
  async function handleResubmit() {
    await resubmitFinding(finding.id);
  }
  function handleToggleVisibility() {
    toggleFindingVisibility(pad.id, finding.idx);
  }
  function handleScrollToNotes() {
    // Expand the card if collapsed, then scroll the notes section into
    // view on the next paint (DOM needs to render the expanded body first).
    if (!effectiveExpanded) setIsExpanded(true);
    requestAnimationFrame(() => {
      notesSectionRef.current?.scrollIntoView();
    });
  }

  // Clean-canvas reanalyze snapshot — same dance as the polygon drawer.
  // drawingPolygon=true hides BOTH QcFindingsLayer and QcCurrentPadLayer
  // (they each subscribe to the flag), so the image has imagery only.
  async function snapshotForReanalyze() {
    if (!mapInstance) throw new Error("Map not ready");
    setDrawingPolygon(true);
    try {
      await new Promise((resolve) => mapInstance.once("idle", resolve));
      const dataUrl = mapInstance.getCanvas().toDataURL("image/jpeg", 0.85);
      const comma = dataUrl.indexOf(",");
      return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
    } finally {
      setDrawingPolygon(false);
    }
  }

  async function handleSubmitCorrect() {
    const text = correctionText.trim();
    // Polygon edit relaxes the text requirement (new boundary is itself
    // a re-evaluation signal); plain correct still requires text.
    if (isReanalyzing) return;
    if (!isEditingThisPolygon && !text) return;

    // Capture the edited polygon from MlDraw BEFORE the snapshot. The
    // snapshot dance (snapshotForReanalyze → setDrawingPolygon true/false)
    // crosses an async boundary on the SHARED draw instance; reading
    // drawInstance.lastFeature afterwards could come back null, which threw
    // "no polygon in MlDraw" and left the finding stuck in edit mode (it
    // never reached submit, so polygonEditMode was never cleared). Read it
    // up front, and fall back to the finding's stored polygon so a save is
    // always possible rather than getting wedged.
    let polygon = null;
    if (isEditingThisPolygon) {
      const drawInstance = useAppViewStore.getState().drawInstance;
      polygon =
        drawInstance?.lastFeature?.geometry ??
        polygonEditMode?.originalPolygon ??
        null;
      if (!polygon || polygon.type !== "Polygon") {
        console.error("[QcFindingCard] polygon edit: no polygon in MlDraw");
        return;
      }
    }

    try {
      const imageB64 = await snapshotForReanalyze();
      if (isEditingThisPolygon) {
        const result = await submitPolygonEdit({
          padIdx,
          findingIdx: finding.idx,
          imageB64,
          operatorCorrection: text,  // may be empty; server synthesizes a placeholder
          polygon,
          mediaType: "image/jpeg",
        });
        if (result.ok) {
          // submitPolygonEdit clears polygonEditMode in the store on success.
          closeCorrect();
          setCorrectionText("");
        }
      } else {
        const ok = await reanalyzeFinding({
          padIdx,
          findingIdx: finding.idx,
          imageB64,
          operatorCorrection: text,
          mediaType: "image/jpeg",
        });
        if (ok) {
          closeCorrect();
          setCorrectionText("");
        }
      }
      // On failure: keep correct mode open + textarea contents intact
      // so operator can edit and retry. Error banner surfaces via the
      // inspector's existing useQcStore.error subscription.
    } catch (err) {
      console.error("[QcFindingCard] reanalyze submit failed", err);
    }
  }

  // Stop click events on action buttons from triggering the card-body
  // toggle. Wrapper keeps the buttons' own onClick logic clean.
  const stopAndRun = (fn) => (e) => { e.stopPropagation(); fn(); };

  // Chip-row action block. Icon-only buttons aligned to the right of
  // the chip row (collapsed: framework/reg/sev/conf; expanded: sev/conf).
  // Always visible — no hover-reveal delay. The vertical hairline
  // separator anchors the icons as a discrete action group rather than
  // blending into the chip flow. shrink-0 keeps the buttons together
  // when chips wrap on narrow viewports.
  const chipActions = (
    <div className="flex items-center gap-1 shrink-0 border-l border-slate-700/60 pl-1.5">
      <button
        type="button"
        onClick={stopAndRun(handleToggleVisibility)}
        className="p-0.5 rounded text-slate-400 hover:text-slate-100 transition"
        title={isHidden ? "Show on map" : "Hide on map"}
      >
        {isHidden ? <EyeOff size={11} /> : <Eye size={11} />}
      </button>
      {noteCount > 0 && (
        <button
          type="button"
          onClick={stopAndRun(handleScrollToNotes)}
          className="p-0.5 rounded text-slate-400 hover:text-slate-100 transition"
          title={`${noteCount} note${noteCount === 1 ? "" : "s"} on this finding`}
        >
          <MessageSquare size={11} />
        </button>
      )}
      <button
        type="button"
        onClick={stopAndRun(handleZoom)}
        className="p-0.5 rounded text-slate-400 hover:text-slate-100 transition"
        title="Zoom to finding"
      >
        <Crosshair size={11} />
      </button>
      <button
        type="button"
        onClick={stopAndRun(handleOpenCorrect)}
        disabled={!canModify}
        className="p-0.5 rounded text-slate-400 hover:text-slate-100 transition
                   disabled:opacity-40 disabled:cursor-not-allowed
                   disabled:hover:text-slate-400"
        title={canModify ? "Correct" : readOnlyTitle}
      >
        <Pencil size={11} />
      </button>
      <GatedAction enabled={canValidate} reason={validateReason}>
        <button
          type="button"
          onClick={stopAndRun(handleValidate)}
          className="p-0.5 rounded text-slate-400 hover:text-emerald-300 transition
                     disabled:opacity-40 disabled:cursor-not-allowed
                     disabled:hover:text-slate-400"
          title="Affirm — this analysis stands as written"
        >
          {isValidatingThis ? <Loader2 size={11} className="animate-spin" /> : <BadgeCheck size={11} />}
        </button>
      </GatedAction>
      <button
        type="button"
        onClick={stopAndRun(handleDismiss)}
        disabled={!canModify}
        className="p-0.5 rounded text-slate-400 hover:text-slate-100 transition
                   disabled:opacity-40 disabled:cursor-not-allowed
                   disabled:hover:text-slate-400"
        title={canModify ? "Dismiss" : readOnlyTitle}
      >
        <XIcon size={11} />
      </button>
      {canRemove && (
        <button
          type="button"
          onClick={stopAndRun(handleOpenRemove)}
          className="p-0.5 rounded text-slate-400 hover:text-rose-300 transition"
          title="Remove finding — persisted, requires a reason, audited"
        >
          <Trash2 size={11} />
        </button>
      )}
      {canKickBack && (
        <button
          type="button"
          onClick={stopAndRun(handleOpenKickback)}
          className="p-0.5 rounded text-slate-400 hover:text-amber-300 transition"
          title="Send this finding back to DreamHammer for rework"
        >
          <RotateCcw size={11} />
        </button>
      )}
      {canResubmit && (
        <button
          type="button"
          onClick={stopAndRun(handleResubmit)}
          disabled={isResubmittingThis}
          className="p-0.5 rounded text-teal-400 hover:text-teal-200 transition
                     disabled:opacity-40 disabled:cursor-not-allowed"
          title="Resubmit this finding to the customer"
        >
          {isResubmittingThis
            ? <Loader2 size={11} className="animate-spin" />
            : <RotateCcw size={11} />}
        </button>
      )}
    </div>
  );

  // Mandatory-rationale modal for persisted removal — shared shell.
  const removeModal = (
    <MandatoryReasonModal
      isOpen={showRemoveModal}
      onClose={() => setShowRemoveModal(false)}
      title={`Remove finding ${finding.idx + 1}`}
      icon={<Trash2 size={14} />}
      tone="rose"
      description="This removes the finding from the active case for everyone — panel, map, counts, exports, and obligations. It is preserved in the audit record and cannot be undone. A reason is required."
      placeholder="Why is this finding being removed? (e.g. flat-wrong AI detection — no feature present)"
      submitLabel="Remove finding"
      submittingLabel="Removing…"
      onSubmit={(reason) => removeFinding(finding.id, reason)}
    />
  );

  // Phase 2 — customer kicks this finding back to DH. Note required, min 10.
  const kickbackModal = (
    <MandatoryReasonModal
      isOpen={showKickbackModal}
      onClose={() => setShowKickbackModal(false)}
      title={`Send finding ${finding.idx + 1} back to DreamHammer`}
      icon={<RotateCcw size={14} />}
      description="DreamHammer will rework this finding and return it for your review. The case stays with you — you can't forward to the operator until it's resolved."
      label="What needs to change?"
      placeholder="Describe what DreamHammer should revise (at least 10 characters)."
      minLength={10}
      submitLabel="Send to DreamHammer"
      submittingLabel="Sending…"
      onSubmit={(note) => kickBackFinding(finding.id, note)}
    />
  );

  // ─── render ─────────────────────────────────────────────────────────────
  return (
    <div
      className={`group rounded border ${cardTone} px-2.5 py-2
                  ${isHidden ? "opacity-60" : ""}`}
    >
      {removeModal}
      {kickbackModal}

      {/* LINE 1 — header. Status pill leads (most informative glance
          signal), Finding N follows as muted secondary text. Trained
          pill + chevron right-aligned. Toggles expand/collapse — the
          handler lives on the title rows (not the whole card) so body
          interactions (attachments, notes, inputs) don't collapse it. */}
      <div
        onClick={handleCardClick}
        className={`flex items-center justify-between gap-2 mb-1 ${isOpen ? "" : "cursor-pointer"}`}
      >
        {/* Line 1 carries IDENTITY only. The status pill moved to line 2 beside
            the feature it describes: it says what the finding IS, not which card
            this is, and it was the widest thing competing with the state group. */}
        <div className="flex items-center gap-1.5 min-w-0 flex-1 overflow-hidden">
          <span className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary whitespace-nowrap shrink-0">
            Finding {finding.idx + 1}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {finding.kicked_back_to_dh && <KickedBackPill />}
          {finding.is_disputed && <DisputedPill />}
          {/* Always visible, collapsed or expanded — so a pad can be scanned for
              review coverage without opening every card. Renders nothing when the
              finding carries no validation. */}
          <ValidationPill validation={finding.validation} />
          {operatorTrained && <CorrectionStagePill history={finding.history} />}
          {/* Chevron — hidden in correct mode (header isn't toggleable there) */}
          {!isOpen && (
            effectiveExpanded
              ? <ChevronUp   size={11} className="text-slate-400" />
              : <ChevronDown size={11} className="text-slate-400" />
          )}
        </div>
      </div>

      {/* LINE 2 — feature. Full width, wraps to 2 lines max, then
          ellipsizes. Always visible when present (collapsed, expanded,
          AND correct mode — operator needs context while editing). */}
      {/* LINE 2 — status + feature. The status pill sits with the text it
          qualifies rather than competing with the state group on line 1, and
          it can wrap here, which it could not up there. */}
      <div className={`flex items-start gap-1.5 mb-1.5 ${isOpen ? "" : "cursor-pointer"}`}
           onClick={handleCardClick}>
        <span className="shrink-0"><StatusPill status={analysis.status} /></span>
        {analysis.feature && (
          <span className="text-[11px] text-slate-100 font-medium leading-snug line-clamp-2 min-w-0">
            {analysis.feature}
          </span>
        )}
      </div>
      {/* Phase A — disputed banner. Shows above analysis content when
          finding.is_disputed is true. Only render in the expanded view
          since it carries actionable text + a button (would be too noisy
          in the collapsed row). */}
      {effectiveExpanded && finding.is_disputed && (
        <DisputedBanner
          finding={finding}
          clearInflight={isClearingThisDispute}
          // The RAISER may withdraw; the CURRENT STAGE'S WRITER may rule, and
          // never on their own objection. caseWriteable is the server's answer
          // to the writer question — not a second copy of STAGE_WRITER here.
          isRaiser={!!effectiveOrg?.id && effectiveOrg.id === finding.disputed_by_org_id}
          canRule={
            caseWriteable === true &&
            !(!!effectiveOrg?.id && effectiveOrg.id === finding.disputed_by_org_id)
          }
          onResolve={(resolution, note) =>
            resolveDispute(finding.id, { resolution, note })
          }
        />
      )}
      {/* Resolved. Rendered whenever a ruling exists and the dispute is closed,
          so the exchange stays on the card instead of vanishing the moment it
          is settled — which is what "clearing" used to do. */}
      {effectiveExpanded && !finding.is_disputed && finding.dispute_resolution && (
        <ResolvedDisputeBanner finding={finding} />
      )}

      {isOpen ? (
        /* ── CORRECT mode ─────────────────────────────────────────────── */
        <div className="space-y-1.5">
          {/* Day 8 Phase 2 — polygon-edit toggle. Opt-in inside correct mode.
              When active, MlDraw shows vertex handles on the map; Submit posts
              the polygon along with any correction text. Phase 2 widened the
              gate (canEditPolygonFE): SP, the customer at customer_review, or
              DH on a kicked-back finding. */}
          {canEditPolygonFE && (
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] font-semibold text-slate-100">
                Describe what this feature actually is:
              </div>
              <button
                type="button"
                onClick={stopAndRun(handleTogglePolygonEdit)}
                disabled={isReanalyzing}
                className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border transition
                            disabled:opacity-40 disabled:cursor-not-allowed
                            ${isEditingThisPolygon
                              ? "bg-amber-500/20 border-amber-500/60 text-amber-200 hover:bg-amber-500/30"
                              : "bg-slate-800/50 border-slate-700/60 text-slate-300 hover:bg-slate-800"}`}
                title={isEditingThisPolygon
                  ? "Polygon edit ACTIVE — click to stop editing"
                  : "Also edit the polygon on the map"}
              >
                <Move size={10} />
                {isEditingThisPolygon ? "Editing polygon" : "Edit polygon"}
              </button>
            </div>
          )}
          {!canEditPolygonFE && (
            <div className="text-[10px] font-semibold text-slate-100">
              Describe what this feature actually is:
            </div>
          )}
          <textarea
            value={correctionText}
            onChange={(e) => setCorrectionText(e.target.value)}
            placeholder={isEditingThisPolygon
              ? "Optional — describe the new boundary's content if helpful."
              : CORRECTION_PLACEHOLDER}
            rows={6}
            disabled={isReanalyzing}
            autoFocus
            className="w-full text-[10px] leading-relaxed
                       bg-slate-900/60 border border-slate-700 rounded
                       px-2 py-1.5 text-slate-200 placeholder:text-slate-500
                       focus:outline-none focus:border-[#16f2d4]/50
                       disabled:opacity-60 disabled:cursor-not-allowed
                       resize-none cursor-text"
          />
          <div className="text-[9px] text-fg-secondary leading-relaxed">
            {isEditingThisPolygon
              ? "Drag vertices to reshape; click the polygon outline to add a vertex; right-click a vertex to remove (≥3 required). Submit re-runs AI with the new boundary."
              : CORRECTION_HINT}
          </div>
          <div className="flex items-center justify-end gap-1.5 pt-0.5">
            <button
              type="button"
              onClick={stopAndRun(handleCancelCorrect)}
              disabled={isReanalyzing}
              className="text-[10px] px-2 py-1 rounded border
                         bg-slate-800/50 border-slate-700/60 text-slate-300
                         hover:bg-slate-800 hover:border-slate-600
                         disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={stopAndRun(handleSubmitCorrect)}
              disabled={
                isReanalyzing ||
                !canModify ||
                (!isEditingThisPolygon && !correctionText.trim())
              }
              className="text-[10px] px-2.5 py-1 rounded border flex items-center gap-1.5
                         bg-[#16f2d4]/15 border-[#16f2d4]/50 text-[#16f2d4]
                         hover:bg-[#16f2d4]/25
                         disabled:opacity-40 disabled:cursor-not-allowed transition"
              title={canModify
                ? (isEditingThisPolygon
                    ? "Save polygon edit + re-run AI"
                    : "Re-run AI with your correction")
                : readOnlyTitle}
            >
              {isReanalyzing
                ? <Loader2 size={10} className="animate-spin" />
                : <RotateCcw size={10} />}
              {isReanalyzing ? "Resubmitting…" : "Submit"}
            </button>
          </div>
        </div>
      ) : effectiveExpanded ? (
        /* ── EXPANDED readout. Body content stacks; sev/conf + the
              icon-only action group share the bottom row, always
              visible (no hover-reveal delay). ───────────────────── */
        <div className="space-y-1.5">
          {timeLabel && (
            <div className="text-[9px] text-fg-secondary tabular-nums">
              {timeLabel}
            </div>
          )}

          {analysis.framework && (
            <div>
              <span className="text-[9px] font-mono uppercase tracking-widest text-slate-400
                               px-1 py-0.5 rounded bg-slate-800/60 border border-slate-700/60">
                {analysis.framework}
              </span>
            </div>
          )}

          {analysis.regulation && (
            <div className="text-[10px] font-mono text-slate-100 leading-snug">
              {analysis.regulation}
            </div>
          )}

          {analysis.description && (
            <div className="text-[10px] text-slate-300 leading-relaxed">
              {analysis.description}
            </div>
          )}

          {operatorTrained && prevStatus && (
            <div className="text-[9px] text-fg-secondary italic">
              previously: {prevStatus}
            </div>
          )}

          {provTrail.length > 1 && (
            <div className="flex items-center gap-1 flex-wrap text-[9px] text-fg-secondary">
              <span className="font-mono uppercase tracking-widest text-fg-secondary">Trail</span>
              {provTrail.map((s, i) => (
                <span key={`${s.role}-${i}`} className="flex items-center gap-1">
                  {i > 0 && <span className="text-slate-700">→</span>}
                  <span
                    className={s.isValidation
                      ? "text-emerald-300/90 flex items-center gap-0.5"
                      : "text-slate-400"}
                    title={[
                      s.isValidation ? "affirmed" : s.event,
                      s.isValidation ? s.userName : null,
                      s.orgName || null,
                      s.caseStage || null,
                      formatTime(s.at) || null,
                    ].filter(Boolean).join(" · ")}
                  >
                    {s.isValidation && <BadgeCheck size={9} className="shrink-0" />}
                    {s.label}
                  </span>
                </span>
              ))}
            </div>
          )}

          <ValidationState validation={finding.validation} />

          {/* The record. No WRITEABILITY gate — see FindingAuditTrail's header
              for why a read-only viewer is the reader this is for. Opens as an
              overlay rather than expanding inline: the panel column is 370px
              and a timeline row does not fit in it.
        
              HIDDEN AT operator_review, and only there. The affirmation stamp
              above stays: an operator seeing WHO affirmed a finding is
              required, and this hides the trail, not the byline.
        
              This is a UI-only control and is deliberately not described as
              more than that. `finding.history` is still in the payload the
              operator receives — the same array this button would render — so
              anyone reading the network response has it. Withholding it would
              be a server change with real consequences: validationState,
              reviewCounts and the disposition gate all derive from history,
              and the audit trail's own claim is completeness. See the report
              accompanying this change. */}
          {currentStage !== "operator_review" && (finding.history?.length ?? 0) > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setAuditOpen(true); }}
              className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-slate-200 underline decoration-dotted underline-offset-2"
            >
              <History className="w-3 h-3" aria-hidden="true" />
              Audit trail ({finding.history.length})
            </button>
          )}
          {auditOpen && currentStage !== "operator_review" && (
            <FindingAuditTrail
              finding={finding}
              padName={pad?.pad_name}
              onClose={() => setAuditOpen(false)}
            />
          )}

          <div className="flex items-start gap-2 justify-between pt-0.5">
            <div className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
              <ChipSev  sev={analysis.severity} />
              <ChipConf conf={analysis.confidence} />
            </div>
            {chipActions}
          </div>

          {/* Operator remediation completion (separate from dispute/VERIFY). */}
          <FindingCompletionSection finding={finding} />

          {/* Finding-level photo evidence (drone/field photos). Photos only;
              documents attach at the pad level. */}
          {caseId && finding.id && pad?.id && (
            <AttachmentSection padId={pad.id} findingId={finding.id} />
          )}

          {/* Day 8 Phase A — per-finding notes section, below all
              regulatory + action chrome. Renders only the "+ Add note"
              trigger when there are zero notes; renders the full list +
              compose box when count > 0 or compose is opened. */}
          {caseId && finding.id && (
            <FindingNotesSection
              ref={notesSectionRef}
              finding={finding}
              caseId={caseId}
            />
          )}
        </div>
      ) : (
        /* ── COLLAPSED — mirrors expanded's row structure for visual
              consistency on toggle:
                Row A: framework + truncated regulation (descriptive,
                       chips only — no icons here so action icons don't
                       collide with truncated reg text).
                Row B: severity + confidence + icon-only action group
                       on the right (decision context + actions).
              Row B always renders so the action icons are always
              reachable, even on cards with no analysis chips. ──── */
        <div
          onClick={handleCardClick}
          className={`space-y-1.5 ${isOpen ? "" : "cursor-pointer"}`}
        >
          {(analysis.framework || shortReg) && (
            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
              {analysis.framework && (
                <span className="text-[9px] font-mono uppercase tracking-widest text-slate-400
                                 px-1 py-0.5 rounded bg-slate-800/60 border border-slate-700/60">
                  {analysis.framework}
                </span>
              )}
              {shortReg && (
                <span className="text-[9px] font-mono text-slate-400 truncate"
                      title={analysis.regulation}>
                  {shortReg}
                </span>
              )}
            </div>
          )}
          <div className="flex items-start gap-2 justify-between">
            <div className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
              <ChipSev  sev={analysis.severity} />
              <ChipConf conf={analysis.confidence} />
            </div>
            {chipActions}
          </div>
        </div>
      )}
    </div>
  );
}
